/**
 * GDSEnvironmentModal.js
 * Handles environment information modal functionality
 */

import { GDSScrollLockManager } from './GDSScrollLockManager.js';

export class GDSEnvironmentModal {
    constructor(modalManager) {
        this.modalManager = modalManager;
        this.builder = modalManager.getBuilder();
    }

    /**
     * Opens an environment info modal by ID
     */
    open(modalId) {
        const modal = document.getElementById(modalId);
        if (!modal) {
            console.warn(`[GDSEnvironmentModal] Modal not found: ${modalId}`);
            return;
        }

        GDSScrollLockManager.lock();

        // Reset modal state
        modal.classList.remove('hidden');

        // Setup animations
        const backdrop = modal.querySelector('.modal-backdrop');
        const content = modal.querySelector('.modal-content');

        // Ensure initial state
        if (backdrop) {
            backdrop.classList.remove('opacity-100');
            backdrop.classList.add('opacity-0');
        }
        if (content) {
            content.classList.remove('scale-100');
            content.classList.add('scale-95');
        }

        // Force reflow
        void modal.offsetHeight;

        // Animate in
        setTimeout(() => {
            if (backdrop) {
                backdrop.classList.remove('opacity-0');
                backdrop.classList.add('opacity-100');
            }
            if (content) {
                content.classList.remove('scale-95');
                content.classList.add('scale-100');
            }
        }, 10);

        // Use common modal opening logic
        this.modalManager.openModal(modal);
    }

    /**
     * Closes an environment info modal
     */
    close(modal) {
        if (!modal) return;

        const backdrop = modal.querySelector('.modal-backdrop');
        const content = modal.querySelector('.modal-content');

        // Apply closing animations
        if (backdrop) {
            backdrop.classList.remove('opacity-100');
            backdrop.classList.add('opacity-0');
        }

        if (content) {
            content.classList.remove('scale-100');
            content.classList.add('scale-95');
        }

        // Wait for animation to complete
        setTimeout(() => {
            modal.classList.add('hidden');

            // Reset styles for next opening
            if (backdrop) {
                backdrop.classList.remove('opacity-0');
            }
            if (content) {
                content.classList.remove('scale-95');
            }

            // Use common modal closing logic
            this.modalManager.closeModal(modal);
            GDSScrollLockManager.unlock();
        }, 300);
    }

    /**
     * Creates HTML for environment info modals
     * This is typically called during step rendering, not by the modal itself
     */
    static createHTML(modalId, title, description) {
        return `
            <div id="${modalId}" class="fixed inset-0 z-50 hidden modal-container flex items-center justify-center p-4" data-modal-type="environment">
                <div class="modal-backdrop absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300 opacity-0" data-backdrop="true"></div>
                <div class="modal-content relative bg-white w-full max-w-lg rounded-lg shadow-xl transform transition-all duration-300 scale-95">
                    <div class="flex items-center justify-between p-4 border-b border-gray-200">
                        <h3 class="text-lg font-semibold text-gray-800">${title}</h3>
                        <button type="button" class="modal-close text-gray-400 hover:text-gray-600 transition-colors">
                            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div class="p-6">
                        <p class="text-gray-600">${description}</p>
                    </div>
                </div>
            </div>
        `;
    }
}
